import {useCallback,useEffect,useMemo,useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography
} from "@mui/material";
import {
  ErrorOutlineRounded,
  RefreshRounded,
  SportsSoccerRounded
} from "@mui/icons-material";
import scoreBackground from "../../static/Matches/score.webp";

const API_BASE="/matches/public/tournaments/nexus-cup-2026";

const COLORS={
  navy:"#06132D",
  white:"#FFFFFF",
  muted:"#B8C9E5",
  border:"rgba(191,216,255,.18)"
};

function getParticipants(calendar){
  const teams=new Map();

  const addTeam=entry=>{
    if(!entry) return;

    const team=entry.team||entry;
    const slug=team.slug;
    const name=team.name;

    if(!slug&&!name) return;

    const key=slug||name.trim().toLowerCase();

    if(!teams.has(key)){
      teams.set(key,{
        slug:slug||key,
        name:name||"Equipo",
        logo:team.logo||null
      });
    }else{
      const existing=teams.get(key);

      if(!existing.logo&&team.logo){
        existing.logo=team.logo;
      }
    }
  };

  for(const match of calendar?.matches||[]){
    addTeam(match.local);
    addTeam(match.visitor);
  }

  for(const round of calendar?.rounds||[]){
    for(const match of round.matches||[]){
      addTeam(match.local);
      addTeam(match.visitor);
    }
  }

  for(const category of calendar?.categories||[]){
    for(const team of category.teams||[]){
      addTeam(team);
    }
  }

  return Array.from(teams.values()).sort((a,b)=>
    a.name.localeCompare(b.name,"es")
  );
}

function TeamLogo({team}){
  const [failed,setFailed]=useState(false);

  useEffect(()=>{
    setFailed(false);
  },[team.logo]);

  return(
    <Box
      sx={{
        width:{xs:76,sm:96,md:115},
        height:{xs:76,sm:96,md:115},
        display:"flex",
        alignItems:"center",
        justifyContent:"center",
        flexShrink:0
      }}
    >
      {team.logo&&!failed?(
        <Box
          component="img"
          src={team.logo}
          alt={`Escudo de ${team.name}`}
          loading="lazy"
          onError={()=>setFailed(true)}
          sx={{
            width:"100%",
            height:"100%",
            display:"block",
            objectFit:"contain"
          }}
        />
      ):(
        <SportsSoccerRounded
          sx={{
            fontSize:{xs:42,sm:52},
            color:"#89A9DA"
          }}
        />
      )}
    </Box>
  );
}

function TeamCard({team}){
  return(
    <Box
      sx={{
        minWidth:0,
        minHeight:{xs:172,sm:210,md:235},
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent:"center",
        gap:{xs:2,md:2.5},
        px:{xs:1.5,sm:2},
        py:{xs:2.5,md:3.5},
        border:`1px solid ${COLORS.border}`,
        borderRadius:{xs:"16px",md:"20px"},
        backgroundColor:"rgba(5,20,48,.65)",
        backdropFilter:"blur(5px)",
        WebkitBackdropFilter:"blur(5px)",
        transition:"background-color .2s ease,border-color .2s ease,transform .2s ease",
        "&:hover":{
          backgroundColor:"rgba(19,48,97,.78)",
          borderColor:"rgba(171,204,255,.46)",
          transform:"translateY(-3px)"
        }
      }}
    >
      <TeamLogo team={team}/>

      <Typography
        sx={{
          width:"100%",
          color:COLORS.white,
          fontSize:{xs:12,sm:14,md:16},
          fontWeight:600,
          lineHeight:1.45,
          textAlign:"center",
          overflowWrap:"anywhere"
        }}
      >
        {team.name}
      </Typography>
    </Box>
  );
}

export default function KnockoutSection(){
  const [calendar,setCalendar]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");

  const loadData=useCallback(async()=>{
    setLoading(true);
    setError("");

    try{
      const response=await fetch(`${API_BASE}/calendar/`);

      if(!response.ok){
        throw new Error("No se pudieron consultar los equipos.");
      }

      const result=await response.json();

      setCalendar(result);
    }catch(err){
      setError(
        err.message||"No se pudieron cargar los equipos participantes."
      );
    }finally{
      setLoading(false);
    }
  },[]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const participants=useMemo(
    ()=>getParticipants(calendar),
    [calendar]
  );

  return(
    <Box
      component="section"
      aria-label="Equipos participantes"
      sx={{
        position:"relative",
        width:"100%",
        backgroundColor:COLORS.navy,
        backgroundImage:`
          linear-gradient(
            180deg,
            rgba(3,12,32,.78) 0%,
            rgba(3,12,32,.68) 48%,
            rgba(3,12,32,.86) 100%
          ),
          url(${scoreBackground})
        `,
        backgroundPosition:"center",
        backgroundSize:"cover",
        backgroundRepeat:"no-repeat",
        py:{xs:7,sm:9,md:11}
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position:"relative",
          px:{xs:2.5,sm:4,md:5},
          py:{xs:4.5,sm:5.5,md:6.5}
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{
            mb:{xs:4,sm:5,md:6}
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize:{xs:29,sm:36,md:43},
              fontWeight:600,
              letterSpacing:"-.035em",
              lineHeight:1.2,
              color:COLORS.white,
              textAlign:"center"
            }}
          >
            Equipos participantes
          </Typography>

        </Stack>

        {loading?(
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{minHeight:240}}
          >
            <CircularProgress
              size={30}
              sx={{color:"#94BCFF"}}
            />

            <Typography
              sx={{
                fontSize:12,
                color:COLORS.muted
              }}
            >
              Cargando equipos...
            </Typography>
          </Stack>
        ):error?(
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{
              minHeight:240,
              textAlign:"center"
            }}
          >
            <ErrorOutlineRounded
              sx={{
                fontSize:36,
                color:"#FFB5BE"
              }}
            />

            <Typography
              sx={{
                fontSize:13,
                color:COLORS.white
              }}
            >
              {error}
            </Typography>

            <Button
              onClick={loadData}
              startIcon={<RefreshRounded/>}
              sx={{
                mt:1,
                px:3,
                py:1.1,
                color:COLORS.white,
                textTransform:"none",
                fontWeight:600,
                borderRadius:"999px",
                border:`1px solid ${COLORS.border}`,
                boxShadow:"none",
                "&:hover":{
                  backgroundColor:"rgba(255,255,255,.1)",
                  boxShadow:"none"
                }
              }}
            >
              Reintentar
            </Button>
          </Stack>
        ):participants.length===0?(
          <Typography
            sx={{
              py:7,
              color:COLORS.muted,
              fontSize:13,
              textAlign:"center"
            }}
          >
            Todavía no hay equipos participantes disponibles.
          </Typography>
        ):(
          <Box
            sx={{
              display:"grid",
              gridTemplateColumns:{
                xs:"repeat(2,minmax(0,1fr))",
                sm:"repeat(3,minmax(0,1fr))",
                md:"repeat(4,minmax(0,1fr))"
              },
              gap:{xs:1.5,sm:2.5,md:3},
              maxWidth:1100,
              mx:"auto"
            }}
          >
            {participants.map(team=>(
              <TeamCard
                key={team.slug}
                team={team}
              />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}